import { Location } from '@angular/common';
import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SetFormActionWithId,
  UpdateFormDataAction,
} from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import {
  FormActionState,
  FormActionStateModel,
} from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { submitDomesticPaymentRequest } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/domestic-payments/submit-payment-request';
import { scrollAndUpdateFragment } from '@app/finxone-web-frontend/app/lib/utils/form-widget-action-utils/input-widget-verify-button-action.utils';
import {
  buildNestedPatch,
  getNestedValue,
} from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';
import {
  addBeneficiary,
  paymentRequest,
  removeBeneficiary,
  updateBeneficiary,
  updateEditIntlBeneficiary,
} from '@app/finxone-web-frontend/app/lib/utils/otp-verification/beneficiary-otp-verification.utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { FormActionTypeEnum, FormID, OtpFlowEnum, OtpParam } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  APP_ZONES,
  BaseWidgetProperties,
  PaymentStatusScreen,
  ProfileStateModel,
  UserProfileData,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, distinctUntilChanged, map, take, takeUntil } from 'rxjs';
import { BaseOtpVerificationComponent } from '../base-otp-verification/base-otp-verification.component';
import { OtpVerificationService } from '@finxone-platform/shared/services';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-otp-verification',
  templateUrl: './otp-verification.component.html',
  styleUrls: ['./otp-verification.component.scss'],
})
export class OtpVerificationComponent implements OnInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  @Input() dataIn: any = {};
  @Input() public widgetConfig: BaseWidgetProperties;
  @Input() public userProfileData: UserProfileData;
  @Input() public contentStyle: baseCssConfigStyle;
  @Input() public inputStyle: baseCssConfigStyle;
  @Input() public textContentStyle: baseCssConfigStyle;
  @Input() public inputBaseStyle: baseCssConfigStyle;
  @Input() public inputInactiveStyle: baseCssConfigStyle;
  @Input() public colorStyle: baseCssConfigStyle;
  @Input() public verifyButtonStyle: baseCssConfigStyle;
  @Input() public resendButtonStyle: baseCssConfigStyle;
  @Input() public buttonContent: string;
  @Input() public resendButtonContent: string;
  @Input() public fieldValue: string;
  @Input() public timerTextStyle: baseCssConfigStyle;
  @Input() public timerTextContent: string;
  @Input() public fieldType: 'email' | 'phone';
  @Input() buttonGlobalStyles = {
    verifyButton: {
      globalStylingClass: '',
      isAllowedGlobalStyling: false,
    },
    resendButton: {
      globalStylingClass: '',
      isAllowedGlobalStyling: false,
    },
  };

  @Select(FormActionState.getFormActionState)
  formActionState$: Observable<FormActionStateModel>;
  formActionState: any;

  @Select(ProfileState.getProfile)
  profile$: Observable<ProfileStateModel>;

  public isVerified = false;
  private flow: string;
  public maxLength: number;

  @ViewChild(BaseOtpVerificationComponent)
  baseOtpComponent: BaseOtpVerificationComponent;
  private otpValue: string;

  constructor(
    private store: Store,
    private location: Location,
    private router: Router,
    private route: ActivatedRoute,
    private injector: Injector,
    public otpVerificationService: OtpVerificationService,
  ) {}

  ngOnInit() {
    this.flow = this.widgetConfig.widgetContext.flow;

    if (this.flow === 'kyc') {
      this.formActionState$
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged(),
          map((response) => {
            this.formActionState = response[FormActionTypeEnum.KYC_DETAILS]?.formData;
          }),
        )
        .subscribe();
    } else if (this.flow === formatNameAsUrl(APP_ZONES.PROFILE)) {
      this.formActionState$
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged(),
          map((response) => {
            this.formActionState = response[FormActionTypeEnum.EDIT_PROFILE_DETAILS]?.formData;
          }),
        )
        .subscribe();
    } else if (this.flow === formatNameAsUrl(APP_ZONES.ONBOARDING)) {
      this.formActionState$
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged(),
          map((response) => {
            this.formActionState = response?.response?.formData;
          }),
        )
        .subscribe();
    } else {
      this.profile$
        .pipe(
          take(1),
          map((response: any) => {
            this.userProfileData = response;
            this.setPageContext();
          }),
        )
        .subscribe();
    }
    this.setPageContext();
  }

  private setPageContext() {
    if (this.fieldType === 'email') {
      this.maxLength = 4;
      this.fieldValue = this.flow === 'payment' ? this.userProfileData?.email : this.formActionState?.email;

      if (this.flow === OtpFlowEnum.ONBOARDING_V2) {
        const formValue = this.store.selectSnapshot(
          FormActionState.getFormActionStateWithId(FormID.ONBOARDING_FLOW),
        );
        this.fieldValue = formValue?.formData?.email;
      } else if (this.flow === OtpFlowEnum.BUSINESS_ONBOARDING) {
        const formValue = this.store.selectSnapshot(
          FormActionState.getFormActionStateWithId(FormID.BUSINESS_ONBOARDING_FLOW),
        );
        this.fieldValue = formValue?.formData.primaryOfficer?.email;
      } else if (this.flow === OtpFlowEnum.UPDATE_USER_PROFILE) {
        const formValue = this.store.selectSnapshot(
          FormActionState.getFormActionStateWithId(FormID.UPDATE_USER_PROFILE),
        );
        this.fieldValue = formValue?.formData?.email ?? this.fieldValue;
      }
    } else if (this.fieldType === 'phone') {
      this.maxLength = 6;
      this.fieldValue =
        this.flow === 'payment'
          ? this.userProfileData?.phoneNumber
          : this.formActionState?.country_code + this.formActionState?.phone;

      if (this.flow === OtpFlowEnum.ONBOARDING_V2) {
        const formValue = this.store.selectSnapshot(
          FormActionState.getFormActionStateWithId(FormID.ONBOARDING_FLOW),
        );
        this.fieldValue = formValue?.formData?.countryCode
          ? formValue?.formData?.countryCode + formValue?.formData?.phoneNumber
          : formValue?.formData?.phoneNumber;
      } else if (this.flow === OtpFlowEnum.BUSINESS_ONBOARDING) {
        const formValue = this.store.selectSnapshot(
          FormActionState.getFormActionStateWithId(FormID.BUSINESS_ONBOARDING_FLOW),
        );
        this.fieldValue = formValue?.formData?.primaryOfficer?.countryCode
          ? formValue?.formData?.primaryOfficer?.countryCode +
            formValue?.formData?.primaryOfficer?.phoneNumber
          : formValue?.formData?.primaryOfficer?.phoneNumber;
      } else if (this.flow === OtpFlowEnum.UPDATE_USER_PROFILE) {
        const formValue = this.store.selectSnapshot(
          FormActionState.getFormActionStateWithId(FormID.UPDATE_USER_PROFILE),
        );
        this.fieldValue =
          (formValue?.formData?.countryCode
            ? formValue?.formData?.countryCode + formValue?.formData?.phoneNumber
            : formValue?.formData?.phoneNumber) ?? this.fieldValue;
      }
    } else {
      console.error('OTP Type not found');
    }
  }

  handleOtpSubmitAndProcess(isVerified: boolean) {
    this.isVerified = isVerified;
    this.route?.queryParams.pipe(take(1)).subscribe((params) => {
      const otpParam = params['otp'];
      if (otpParam && this.isVerified) {
        this.handleRedirection(otpParam);
      } else {
        this.setValueVerify();
      }
    });
  }

  public otpSubmissionHandler(event: { otpParam: string; code: string }) {
    this.otpValue = event.code;
    this.handleApiCallAndRedirection(event.otpParam);
  }

  private handleApiCallAndRedirection(otpParam: string) {
    const actions = new Map<string, () => void>([
      [OtpParam.PaymentRequest, () => paymentRequest(this.injector)],
    ]);

    const otpAction = actions.get(otpParam);
    if (otpAction) {
      otpAction();
    }
  }

  private handleRedirection(otpParam: string) {
    const actions = new Map<string, () => void>([
      [OtpParam.EditBeneficiary, () => updateBeneficiary(this.injector)],
      [OtpParam.EditIntlBeneficiary, () => updateEditIntlBeneficiary(this.injector)],
      [OtpParam.AddBeneficiary, () => addBeneficiary(this.injector)],
      [OtpParam.RemoveBeneficiary, () => removeBeneficiary(this.injector)],
    ]);

    const otpAction = actions.get(otpParam);
    if (otpAction) {
      otpAction();
    }
  }

  private setValueVerify() {
    if (
      this.baseOtpComponent.widgetConfig.widgetContext.flow === 'payment' &&
      this.baseOtpComponent.fieldType === 'phone'
    ) {
      this.navigateToPaymentStatusScreen();
      this.onSubmitPaymentRequest();
      return;
    }
    this.dispatchActionByFlowTypeAndRedirect(
      this.baseOtpComponent.widgetConfig.widgetContext.flow,
      this.baseOtpComponent.fieldType,
    );
  }

  private navigateToPaymentStatusScreen(): void {
    this.router.navigateByUrl(`/zones/payments/${PaymentStatusScreen.PAYMENT_PROCESSING}`, {
      replaceUrl: true,
    });
  }

  private dispatchActionByFlowTypeAndRedirect(flow: string, field: 'email' | 'phone') {
    const fieldKey = field === 'email' ? 'isEmailVerified' : 'isPhoneVerified';
    if (flow === 'kyc') {
      this.store.dispatch(
        new SetFormActionWithId(
          { type: '', formData: { [fieldKey]: this.isVerified } },
          FormActionTypeEnum.KYC_DETAILS,
        ),
      );
    }

    if (flow === OtpFlowEnum.UPDATE_USER_PROFILE) {
      this.store.dispatch(
        new SetFormActionWithId(
          { type: '', formData: { [fieldKey]: this.isVerified } },
          FormID.UPDATE_USER_PROFILE,
        ),
      );
    }

    if (flow === 'profile') {
      this.store.dispatch(
        new SetFormActionWithId(
          { type: '', formData: { [fieldKey]: this.isVerified } },
          FormActionTypeEnum.EDIT_PROFILE_DETAILS,
        ),
      );
    }
    if (flow === 'onboarding') {
      this.store.dispatch(
        new UpdateFormDataAction({
          formData: { [fieldKey]: this.isVerified },
        }),
      );
    }

    if (flow === OtpFlowEnum.ONBOARDING_V2) {
      const formValue = this.store.selectSnapshot(
        FormActionState.getFormActionStateWithId(FormID.ONBOARDING_FLOW),
      );
      const formData: Record<string, unknown> = { [fieldKey]: this.isVerified };
      this.addOnboardingVerificationMarkers(formData, field, this.fieldValue, formValue?.formData);
      this.store.dispatch(new SetFormActionWithId({ type: '', formData }, FormID.ONBOARDING_FLOW));
    }
    if (flow === OtpFlowEnum.BUSINESS_ONBOARDING) {
      const formValue = this.store.selectSnapshot(
        FormActionState.getFormActionStateWithId(FormID.BUSINESS_ONBOARDING_FLOW),
      );
      const formData: Record<string, unknown> = { [fieldKey]: this.isVerified };
      const primaryOfficer = getNestedValue<Record<string, unknown>>(formValue?.formData, 'primaryOfficer');
      this.addOnboardingVerificationMarkers(formData, field, this.fieldValue, primaryOfficer);

      // Primary-officer contact details are collected inside a nested group on the business form,
      // so the same verification markers must live on that object too. Otherwise phone/email
      // widgets bound to primaryOfficer won't see isPhoneVerified/isEmailVerified and won't show
      // the green tick when the user navigates back.
      let dispatchFormData: Record<string, unknown> = formData;
      if (primaryOfficer) {
        const primaryOfficerMarkers: Record<string, unknown> = { [fieldKey]: this.isVerified };
        this.addOnboardingVerificationMarkers(primaryOfficerMarkers, field, this.fieldValue, primaryOfficer);
        dispatchFormData = {
          ...buildNestedPatch(formValue?.formData as Record<string, unknown>, 'primaryOfficer', {
            ...primaryOfficer,
            ...primaryOfficerMarkers,
          }),
          ...formData,
        };
      }

      this.store.dispatch(
        new SetFormActionWithId({ type: '', formData: dispatchFormData }, FormID.BUSINESS_ONBOARDING_FLOW),
      );
    }

    this.otpVerificationService.emitVerification({
      field: this.fieldType,
      value: this.fieldValue,
      verified: this.isVerified,
    });

    this.location.back();
    scrollAndUpdateFragment(this.router);
  }

  private onSubmitPaymentRequest(): void {
    submitDomesticPaymentRequest(this.injector, this.widgetConfig);
  }

  private addOnboardingVerificationMarkers(
    formData: Record<string, unknown>,
    field: 'email' | 'phone',
    fieldValue: string,
    source: Record<string, unknown> | undefined,
  ): void {
    const verifiedAt = new Date().toISOString();
    if (field === 'email') {
      formData['emailVerifiedAt'] = verifiedAt;
      formData['verifiedEmail'] = fieldValue;
    } else {
      formData['phoneVerifiedAt'] = verifiedAt;
      formData['verifiedPhone'] = fieldValue;
      formData['verifiedCountryCode'] = source?.['countryCode'] ?? source?.['verifiedCountryCode'];
    }
  }
}
